/**
 * Formie Form Tracking
 *
 * PURPOSE:
 * Track form submissions for various form types:
 * - Job applications (purchase event)
 * - Spontaneous applications (purchase event)
 * - Contact forms (generate_lead event)
 * - Registration (sign_up event)
 * - Login (login event)
 * - Newsletter (user_info_set event)
 *
 * HOW FORMIE WORKS:
 * Formie 3 dispatches custom events on the <form> element (they bubble):
 * - 'onAfterFormieSubmit' - after every successful ajax submit, including
 *   intermediate pages of multi-page forms; `detail.nextPageId` is set on
 *   page submits and empty on final completion. The form is `event.target`.
 * - 'onFormieSubmitError' - Form submission failed
 *
 * We listen for the final successful submit and track accordingly.
 */

import {
    trackPurchase,
    trackSpontaneousApplication,
    trackSignUp,
    trackLogin,
    trackGenerateLead
} from '~/js/utils/job-data-layer'

import { hasConsent } from '~/js/utils/datalayer'

declare global {
    interface Window {
        __GO4JOBS_PAGE__?: {
            type: string;
            transactionId?: string;
            job?: Record<string, any>;
        };
    }
}

/**
 * Form handles and their tracking types
 */
const FORM_CONFIG: Record<string, {
    type: 'application' | 'spontaneous' | 'register' | 'login' | 'contact' | 'newsletter';
    leadType?: 'contact_office' | 'contact_go4jobs' | 'staffing_entrepreneur';
}> = {
    // Job applications
    'applicationForm': { type: 'application' },
    'applicationFormRegisteredUser': { type: 'application' },

    // Spontaneous applications
    'spontaneousApplicationForm': { type: 'spontaneous' },
    'spontaneousApplicationFormRegisteredUser': { type: 'spontaneous' },

    // Users
    'register': { type: 'register' },
    'login': { type: 'login' },

    // Contact forms
    'contactForm': { type: 'contact', leadType: 'contact_office' },
    'companyContactForm': { type: 'contact', leadType: 'contact_go4jobs' },
    'staffingEntrepreneurForm': { type: 'contact', leadType: 'staffing_entrepreneur' },

    // Newsletter
    'subscribe': { type: 'newsletter' }
};

/**
 * Get form handle from a Formie form element
 *
 * Formie forms use ID format: fui-{formHandle}-{randomId}
 * e.g., fui-applicationForm-ohmdbn -> applicationForm
 */
function getFormHandle(form: HTMLFormElement): string | null {
    // Try data-form-handle attribute first
    const handleAttr = form.getAttribute('data-form-handle');
    if (handleAttr) return handleAttr;

    // Extract from form ID: fui-{handle}-{randomId}
    const formId = form.id;
    if (formId && formId.startsWith('fui-')) {
        // Remove 'fui-' prefix and the random suffix
        const parts = formId.substring(4).split('-');
        if (parts.length >= 2) {
            // Remove the last part (random ID) and join the rest
            parts.pop();
            return parts.join('-');
        }
    }

    return null;
}

/**
 * Initialize Formie tracking
 */
export function initFormTracking(): void {
    // For AJAX submissions - Formie fires this event
    document.addEventListener('onAfterFormieSubmit', handleFormieSuccess as EventListener);

    // For page-reload submissions - stash tracking data before page navigates away
    document.querySelectorAll('form[data-fui-form]').forEach(form => {
        form.addEventListener('submit', () => {
            // Stash purchase data synchronously before the page reloads
            const formHandle = getFormHandle(form as HTMLFormElement);
            const config = formHandle ? FORM_CONFIG[formHandle] : null;
            const pageData = window.__GO4JOBS_PAGE__;

            if (config && (config.type === 'application' || config.type === 'spontaneous') && pageData) {
                const formData = new FormData(form as HTMLFormElement);
                const email = (formData.get('email') || formData.get('fields[email]')) as string | null;
                const phone = (formData.get('phone') || formData.get('fields[phone]') || formData.get('fields[phone][number]') || formData.get('fields[mobile]') || formData.get('fields[mobile][number]')) as string | null;

                sessionStorage.setItem('__go4jobs_pending_purchase', JSON.stringify({
                    type: config.type,
                    job: pageData.job ?? null,
                    transactionId: pageData.transactionId
                        ?? (config.type === 'spontaneous'
                            ? `spontaneous_${Date.now()}`
                            : `${pageData.job?.item_id}_${Date.now()}`),
                    email: email || null,
                    phone: phone || null,
                }));
            }
        });
    });
}

/**
 * Handle Formie AJAX success event
 * This fires for forms using AJAX submission
 */
function handleFormieSuccess(event: CustomEvent): void {
    // Multi-page forms fire this on every page submit — only track the final one
    if (event.detail?.nextPageId) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    handleFormSubmit(form, event.detail);
    // AJAX tracking succeeded — clear stash so thank-you page doesn't double-fire
    sessionStorage.removeItem('__go4jobs_pending_purchase');
}

/**
 * Handle form submission
 *
 * Called either from:
 * 1. Native submit event (page-reload forms) - form data is available
 * 2. Formie success event (AJAX forms) - may include response object
 */
function handleFormSubmit(form: HTMLFormElement, response?: any): void {
    // Get form handle
    const formHandle = getFormHandle(form);
    if (!formHandle) return;

    // Get form configuration
    const config = FORM_CONFIG[formHandle];
    if (!config) return;

    // Extract form data
    const formData = new FormData(form);

    // Handle based on form type
    switch (config.type) {
        case 'application':
            handleApplicationSubmit(formData, response);
            break;

        case 'spontaneous':
            handleSpontaneousSubmit(formData, response);
            break;

        case 'register':
            handleRegisterSubmit(formData, response);
            break;

        case 'login':
            handleLoginSubmit();
            break;

        case 'contact':
            handleContactSubmit(formData, config.leadType!, response);
            break;

        case 'newsletter':
            handleNewsletterSubmit(formData, response);
            break;
    }
}

/**
 * Handle job application submission
 */
async function handleApplicationSubmit(formData: FormData, response?: any): Promise<void> {
    // Get job data from page context
    const pageData = window.__GO4JOBS_PAGE__;
    if (!pageData?.job) return;

    // Read transaction ID from server-generated page data, fall back to client-generated
    const transactionId = pageData.transactionId
        ?? `${pageData.job.item_id}_${Date.now()}`;

    // Extract and hash user data
    const userData = hasConsent('marketing')
        ? await extractUserData(formData, response)
        : null;

    // Track purchase with hashed user data (userData is null if no data found)
    trackPurchase(transactionId, pageData.job, userData ?? undefined);
}

/**
 * Handle spontaneous application submission
 */
async function handleSpontaneousSubmit(formData: FormData, response?: any): Promise<void> {
    // Read transaction ID from server-generated page data, fall back to client-generated
    const transactionId = window.__GO4JOBS_PAGE__?.transactionId
        ?? `spontaneous_${Date.now()}`;
    const userData = await extractUserData(formData, response);

    trackSpontaneousApplication(transactionId, userData ?? undefined);
}

/**
 * Handle registration submission
 */
function handleRegisterSubmit(formData: FormData, response?: any): void {
    trackSignUp('email');
}

/**
 * Handle login submission
 */
function handleLoginSubmit(): void {
    trackLogin('email');
}

/**
 * Handle contact form submission
 */
function handleContactSubmit(
    formData: FormData,
    leadType: 'contact_office' | 'contact_go4jobs' | 'staffing_entrepreneur',
    response?: any
): void {
    // Get office name if available
    const office = getFieldValue(formData, response, 'office');

    trackGenerateLead(leadType, office || undefined);
}

/**
 * Handle newsletter submission
 */
function handleNewsletterSubmit(formData: FormData, response?: any): void {}

/**
 * Get a field value from FormData or Formie response object
 *
 * Formie response object may contain submission data in various formats.
 * This function tries multiple sources to find the field value.
 */
function getFieldValue(formData: FormData, response: any, fieldHandle: string): string | undefined {
    // Try FormData first (with various naming conventions)
    let value = (
        formData.get(fieldHandle) ||
        formData.get(`fields[${fieldHandle}]`) ||
        formData.get(`fields[${fieldHandle}][number]`)  // For Phone fields
    ) as string | undefined;

    // If not found in FormData, try response object
    if (!value && response) {
        // Try different response structures
        // Formie might return data in response.submission.fields or similar
        const submission = response.submission || response;

        if (submission?.fields?.[fieldHandle]) {
            value = submission.fields[fieldHandle];
        } else if (submission?.[fieldHandle]) {
            value = submission[fieldHandle];
        }
    }

    return value || undefined;
}

/**
 * Hash a string using SHA-256
 * Uses the Web Crypto API (available in all modern browsers)
 */
async function sha256(str: string): Promise<string> {
    const buffer = new TextEncoder().encode(str)
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Normalize and hash email per Google's spec
 * - Lowercase
 * - Trim whitespace
 * - SHA256 hash
 */
async function hashEmail(email: string): Promise<string> {
    const normalized = email.toLowerCase().trim()
    return sha256(normalized)
}

/**
 * Normalize and hash phone per Google's spec
 * - Remove spaces, dashes, parentheses
 * - Include country code (e.g., +32 for Belgium)
 * - SHA256 hash
 */
async function hashPhone(phone: string): Promise<string> {
    // Remove all non-digit characters except leading +
    let normalized = phone.replace(/[^\d+]/g, '')

    // Add Belgium country code if not present
    if (!normalized.startsWith('+')) {
        normalized = '+32' + normalized.replace(/^0/, '')
    }

    return sha256(normalized)
}

/**
 * Extract and hash user data from form
 *
 * Formie forms use the naming convention "fields[fieldHandle]" for field names.
 * We check both formats: "email" and "fields[email]" for compatibility.
 * Phone fields in Formie may use "fields[phone][number]" format.
 *
 * Also checks the Formie response object as a fallback in case form inputs
 * have been cleared by the time this function is called.
 */
async function extractUserData(formData: FormData, response?: any): Promise<{
    sha256_email_address?: string
    sha256_phone_number?: string
} | null> {
    // Get email and phone using helper that checks FormData and response
    const email = getFieldValue(formData, response, 'email');
    const phone = getFieldValue(formData, response, 'phone') ||
                  getFieldValue(formData, response, 'mobile');

    const userData: Record<string, string> = {}

    if (email) {
        userData.sha256_email_address = await hashEmail(email)
    }

    if (phone) {
        userData.sha256_phone_number = await hashPhone(phone)
    }

    // Return null if no user data was found (avoids sending empty user_data object)
    return Object.keys(userData).length > 0 ? userData : null
}

/**
 * Initialize
 */
if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initFormTracking);
    } else {
        initFormTracking();
    }
}
